import React, { useState } from 'react';
import {
  Megaphone,
  User,
  ChevronDown,
  ChevronUp,
  Pin,
  AlertCircle,
  Calendar,
  MessageSquare,
  Bell,
} from 'lucide-react';
import { useModuleDetailsQuery, CourseModule } from '../../../api/studentCoursesApi';
import { tokenService } from '../../../services/TokenService';

/* ─── Types ──────────────────────────────────────────────────────────────── */

interface ForumDiscussionPost {
  id: number;
  subject: string;
  message?: string;
  author: string;
  authorId?: number;
  authorPicture?: string;
  created: number;
  modified: number;
  numreplies: number;
  pinned?: boolean;
  locked?: boolean;
  unread?: boolean;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function formatDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(ts: number): string {
  return new Date(ts * 1000).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts * 1000;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 30) return formatDate(ts);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

function sanitizeHtml(html: string): string {
  return html
    .replace(/style="[^"]*"/gi, '')
    .replace(/<img /gi, '<img style="max-width:100%;height:auto;border-radius:8px;" ');
}

/* Deterministic avatar bg from author name — muted professional palette */
const AVATAR_COLORS = [
  'from-slate-600 to-slate-700',
  'from-blue-600 to-blue-700',
  'from-sky-600 to-cyan-700',
  'from-teal-600 to-teal-700',
  'from-indigo-600 to-indigo-700',
];
function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/* ─── Shared card inner layout (extracted to avoid duplication) ─────────── */

function PinnedContent({
  post,
  hasBody,
  expanded,
  setExpanded,
}: {
  post: ForumDiscussionPost;
  hasBody: boolean;
  expanded: boolean;
  setExpanded: (fn: (v: boolean) => boolean) => void;
}) {
  return (
    <>
      <button
        className="w-full text-left px-5 py-4 flex items-start gap-4 group focus:outline-none"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {/* Avatar */}
        <div className="shrink-0">
          {post.authorPicture ? (
            <img
              src={post.authorPicture}
              alt={post.author}
              className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow-sm"
            />
          ) : (
            <div
              className={`h-10 w-10 rounded-full bg-linear-to-br ${avatarColor(post.author)} flex items-center justify-center text-white text-sm font-bold shadow-sm`}
            >
              {post.author.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            {post.pinned && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#4F6BFF] border px-2 py-0.5 rounded-full shrink-0 uppercase tracking-wide" style={{ background: '#EEF2FF', borderColor: '#E2E8F0' }}>
                <Pin className="h-2.5 w-2.5" />
                Pinned
              </span>
            )}
            <h3
              className={`text-sm font-semibold leading-snug transition-colors ${
                expanded ? 'text-[#4F6BFF]' : 'text-slate-900 group-hover:text-[#4F6BFF]'
              }`}
            >
              {post.subject}
            </h3>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
            <span className="flex items-center gap-1 text-xs font-medium text-slate-600">
              <User className="h-3 w-3 text-slate-400" />
              {post.author}
            </span>
            <span className="text-slate-300 text-xs">·</span>
            <span
              className="flex items-center gap-1 text-xs text-slate-400"
              title={formatDateTime(post.created)}
            >
              <Calendar className="h-3 w-3" />
              {timeAgo(post.created)}
            </span>
            {post.numreplies > 0 && (
              <>
                <span className="text-slate-300 text-xs">·</span>
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <MessageSquare className="h-3 w-3" />
                  {post.numreplies} {post.numreplies === 1 ? 'reply' : 'replies'}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Chevron */}
        {hasBody && (
          <div className="shrink-0 mt-0.5">
            <div
              className={`h-6 w-6 rounded-md flex items-center justify-center transition-colors ${
                expanded
                  ? 'text-[#4F6BFF] bg-[#EEF2FF]'
                  : 'bg-slate-100 text-slate-400 group-hover:bg-[#EEF2FF] group-hover:text-[#4F6BFF]'
              }`}
            >
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </div>
          </div>
        )}
      </button>

      {/* Expanded body */}
      {expanded && hasBody && (
        <div className="px-5 pb-5">
          <div className="border-t border-slate-100 pt-4">
            <div
              className="prose prose-sm max-w-none text-slate-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.message!) }}
            />
            <p className="text-[11px] text-slate-400 mt-4">
              Posted {formatDateTime(post.created)}
              {post.modified !== post.created && ` · Edited ${timeAgo(post.modified)}`}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Announcement Card ──────────────────────────────────────────────────── */

function AnnouncementCard({
  post,
  index,
}: {
  post: ForumDiscussionPost;
  index: number;
}) {
  const [expanded, setExpanded] = useState(index === 0);
  const hasBody =
    !!post.message && post.message.replace(/<[^>]+>/g, '').trim().length > 0;

  return (
    <div
      className="rounded-xl border overflow-hidden transition-all duration-200 bg-white shadow-sm hover:shadow-md"
      style={{ borderColor: post.pinned ? '#4F6BFF' : '#E2E8F0' }}
    >
      {/* Pinned left accent bar */}
      {post.pinned && (
        <div className="flex">
          <div className="w-1 shrink-0 rounded-l-xl" style={{ background: '#4F6BFF' }} />
          <div className="flex-1">
            <PinnedContent
              post={post}
              hasBody={hasBody}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          </div>
        </div>
      )}
      {!post.pinned && (
        <PinnedContent
          post={post}
          hasBody={hasBody}
          expanded={expanded}
          setExpanded={setExpanded}
        />
      )}
    </div>
  );
}

/* ─── Skeleton loader ────────────────────────────────────────────────────── */

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 space-y-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-gray-100 animate-pulse shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 bg-gray-100 animate-pulse rounded-full w-2/3" />
          <div className="h-2.5 bg-gray-100 animate-pulse rounded-full w-1/3" />
        </div>
      </div>
      <div className="space-y-1.5 pl-13">
        <div className="h-2.5 bg-gray-100 animate-pulse rounded-full w-full" />
        <div className="h-2.5 bg-gray-100 animate-pulse rounded-full w-5/6" />
        <div className="h-2.5 bg-gray-100 animate-pulse rounded-full w-4/6" />
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

interface ForumModuleContentProps {
  contextId: string;
  moduleId: string;
  module: CourseModule | null;
  headerColor?: string;
}

const ForumModuleContent: React.FC<ForumModuleContentProps> = ({
  contextId,
  moduleId,
  module,
  headerColor,
}) => {
  const token = tokenService.getToken();

  const { data: forumDetails, isLoading, error } = useModuleDetailsQuery(
    contextId,
    moduleId,
    'forum',
    token,
    { enabled: !!contextId && !!moduleId },
  );

  const rawDiscussions: ForumDiscussionPost[] =
    (forumDetails as any)?.discussions ?? [];

  const discussions = [...rawDiscussions].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return b.created - a.created;
  });

  const forumName = forumDetails?.name || module?.name || 'Announcements';
  const intro = forumDetails?.intro || module?.description || '';

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Header skeleton */}
        <div className="rounded-2xl border border-gray-100 bg-white p-6 flex items-center gap-4 shadow-sm">
          <div className="h-12 w-12 rounded-xl bg-gray-100 animate-pulse shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-100 animate-pulse rounded-full w-40" />
            <div className="h-2.5 bg-gray-100 animate-pulse rounded-full w-24" />
          </div>
        </div>
        {[0, 1, 2].map((i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-10 text-center">
        <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
          <AlertCircle className="h-6 w-6 text-red-500" />
        </div>
        <p className="font-semibold text-gray-800 mb-1">Couldn't load announcements</p>
        <p className="text-sm text-gray-500">{(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Hero header ── */}
      <div className="rounded-xl overflow-hidden p-6 shadow-sm border border-gray-200" style={{ background: '#F9FAFB', borderTop: `4px solid ${headerColor || '#374151'}` }}>
        <div className="flex items-start gap-4">
          <div className="rounded-lg p-3 shrink-0 shadow-sm" style={{ background: headerColor ? `${headerColor}1A` : '#E5E7EB' }}>
            <Bell className="h-6 w-6" style={{ color: headerColor || '#374151' }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold leading-tight tracking-tight text-gray-900">{forumName}</h2>
            {intro ? (
              <div
                className="mt-1.5 text-sm text-gray-500 leading-relaxed line-clamp-2"
                dangerouslySetInnerHTML={{ __html: intro }}
              />
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                Stay up to date with the latest updates from your instructor.
              </p>
            )}
            <div className="mt-3 inline-flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-full px-3 py-1 text-xs font-medium text-gray-600">
              <Megaphone className="h-3.5 w-3.5 text-gray-400" />
              {discussions.length} announcement{discussions.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* ── Announcements list ── */}
      {discussions.length > 0 ? (
        <div className="space-y-3">
          {discussions.map((post, idx) => (
            <AnnouncementCard key={post.id} post={post} index={idx} />
          ))}
        </div>
      ) : (
        /* ── Empty state ── */
        <div className="rounded-xl px-6 py-14 flex flex-col items-center text-center" style={{ background: '#F8FAFF', border: '1.5px solid #E2E8F0' }}>
          <div className="h-16 w-16 rounded-xl flex items-center justify-center mb-4 shadow-sm" style={{ background: '#EEF2FF', border: '1.5px solid #E2E8F0' }}>
            <Bell className="h-8 w-8 text-[#4F6BFF]" />
          </div>
          <p className="font-semibold text-slate-700">No announcements yet</p>
          <p className="text-sm text-slate-400 max-w-xs mt-1">
            Your instructor hasn't posted any announcements yet. Check back later.
          </p>
        </div>
      )}
    </div>
  );
};

export default ForumModuleContent;
